import { Actor, Canvas, Engine, Random, vec } from 'excalibur';
import { ThemePaletteKey, getTheme } from '../themes/theme';
import { approach, normalizeDegrees, polarOffset, toRadians } from '../utils/angles';
import { MARKER_RADIUS } from '../utils/constants';
import { css, drawSparkle, sketchStroke, wobblyCirclePath, wobblyLinePath } from '../utils/sketch';

export interface MarkerOptions {
  name: string;
  tone: ThemePaletteKey;
  deepTone: ThemePaletteKey;
  guideLength: number;
  seed: number;
}

export class Marker extends Actor {
  private readonly tone: ThemePaletteKey;
  private readonly deepTone: ThemePaletteKey;
  private readonly guideLength: number;
  private readonly seed: number;
  private readonly phase: number;
  private hotTarget = 0;
  private hotAmount = 0;
  private flashTimer = 0;
  private time = 0;
  angleDeg = 0;

  constructor(options: MarkerOptions) {
    super({
      name: options.name,
      graphic: new Canvas({
        width: 176,
        height: 176,
        cache: false,
        draw: (ctx) => this.paint(ctx),
      }),
    });
    this.tone = options.tone;
    this.deepTone = options.deepTone;
    this.guideLength = options.guideLength;
    this.seed = options.seed;
    const rng = new Random(options.seed);
    this.phase = rng.floating(0, Math.PI * 2);
  }

  setAngle(degrees: number): void {
    this.angleDeg = normalizeDegrees(degrees);
    this.pos = polarOffset(MARKER_RADIUS, this.angleDeg);
  }

  setHot(hot: boolean): void {
    this.hotTarget = hot ? 1 : 0;
  }

  flash(): void {
    this.flashTimer = 1;
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    this.time += dt;
    this.hotAmount = approach(this.hotAmount, this.hotTarget, dt * 8);
    this.flashTimer = Math.max(0, this.flashTimer - dt * 2.4);
    const pulse = 1 + Math.sin(this.time * 3.4 + this.phase) * 0.08;
    const growth = 1 + this.hotAmount * 0.3 + this.flashTimer * 0.25;
    this.scale = vec(pulse * growth, pulse * growth);
    this.rotation = toRadians(this.angleDeg + Math.sin(this.time * 2.6 + this.phase) * 2.4);
  }

  private paint(ctx: CanvasRenderingContext2D): void {
    const rng = new Random(this.seed + 17);
    const palette = getTheme().palette;
    const markerColor = palette[this.tone];
    const deepColor = palette[this.deepTone];
    const mid = 88;
    const guideStart = mid + 28;
    const guideEnd = mid + this.guideLength;
    const dashes = Math.max(3, Math.round((guideEnd - guideStart) / 18));
    for (let i = 0; i < dashes; i++) {
      const t0 = i / dashes;
      const t1 = t0 + 0.55 / dashes;
      const y0 = guideStart + (guideEnd - guideStart) * t0;
      const y1 = guideStart + (guideEnd - guideStart) * t1;
      sketchStroke(ctx, rng, { color: deepColor, width: 5, alpha: 0.28 }, (c) =>
        wobblyLinePath(c, mid, y0, mid, y1, rng, 1.2, 3), 1);
    }

    const glowRadius = 22 + this.hotAmount * 8 + this.flashTimer * 12;
    ctx.save();
    for (let i = 6; i >= 1; i--) {
      ctx.fillStyle = css(markerColor, 0.04 + this.hotAmount * 0.02);
      wobblyCirclePath(ctx, mid, mid, glowRadius + i * 4.5, rng, 1.8, 28);
      ctx.fill();
    }
    ctx.restore();

    sketchStroke(ctx, rng, { color: deepColor, width: 3.4, alpha: 0.45 + this.hotAmount * 0.5 }, (c) =>
      wobblyCirclePath(c, mid, mid, 16, rng, 1.4, 24), 2, 0.8);

    ctx.save();
    ctx.fillStyle = css(markerColor, 0.95);
    wobblyCirclePath(ctx, mid, mid, 10.5, rng, 1.1, 20);
    ctx.fill();
    ctx.fillStyle = css(palette.cream, 0.85);
    ctx.beginPath();
    ctx.arc(mid - 3, mid - 3.5, 2.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    const sparkle = Math.max(this.hotAmount, this.flashTimer);
    if (sparkle > 0.08) {
      drawSparkle(ctx, mid + 27, mid - 24, 5, rng, deepColor, sparkle * 0.9);
      drawSparkle(ctx, mid - 29, mid + 19, 4, rng, palette.yellowDeep, sparkle * 0.8);
    }
  }
}
